import { useState, useMemo } from "react";
import { RiDeleteBinFill, RiPencilLine, RiRefreshLine, RiUserAddLine } from "react-icons/ri";
import Typography from "../../../Utility/Typography";
import Button from "../../../Utility/Button/Button";
import FloatingTooltip from "../../../Utility/FloatingTooltip/FloatingTooltip";
import CommonModal from "../../../Utility/CommonModel/CommonModal";
import Input from "../../../Utility/Input/Input";
import Loader from "../../../Utility/Loader/Loader";

const ROLE_LABELS = {
  Admin: "Admin",
  Member: "Member",
  Viewer: "Viewer",
};

const STATUS_LABELS = {
  Active: "Active",
  Invited: "Invited",
  Inactive: "Inactive",
  ACTIVE: "Active",
  INACTIVE: "Inactive",
};

/** Prefer API firstName + lastName; fall back to legacy `name`. */
function getTeamMemberDisplayName(member) {
  if (!member) return "—";
  const fromParts = [member.firstName, member.lastName].filter(Boolean).join(" ").trim();
  if (fromParts) return fromParts;
  const legacy = member.name != null ? String(member.name).trim() : "";
  if (legacy) return legacy;
  return "—";
}

const getStatusBadgeClass = (status) => {
  switch (status) {
    case "Active":
    case "ACTIVE":
      return "bg-badge-success";
    case "Invited":
      return "bg-badge-warning";
    case "Inactive":
    case "INACTIVE":
      return "bg-badge-danger";
    default:
      return "bg-badge-secondary";
  }
};

const ViewTeamMembersModal = ({
  isOpen,
  onClose,
  team,
  members = [],
  /** True while GET /teams/:id/users is in flight (parent sets from fetch). */
  membersLoading = false,
  onAddMember,
  onEditMember,
  onResendInvitation,
  onRemoveMember,
}) => {
  const [filterText, setFilterText] = useState("");

  const filteredMembers = useMemo(() => {
    if (!filterText.trim()) return members;
    const q = filterText.toLowerCase();
    return members.filter((m) => {
      const display = getTeamMemberDisplayName(m);
      const nameHaystack = (display === "—" ? "" : display).toLowerCase();
      return nameHaystack.includes(q) || (m.email && m.email.toLowerCase().includes(q));
    });
  }, [members, filterText]);

  if (!isOpen) return null;

  const teamName = team?.teamName || "—";

  return (
    <CommonModal isOpen={isOpen} onClose={onClose} title="" width="max-w-3xl">
      <div className="flex flex-col gap-4">
        {/* Header: Title, Sub-title, + Add Member */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border-primary pb-3 -mt-1">
          <div>
            <Typography variant="h5" className="font-semibold text-text-primary">
              Team Members
            </Typography>
            <Typography variant="text2" className="text-text-secondary mt-0.5">
              Team: {teamName}
            </Typography>
          </div>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={onAddMember}
            className="btn-success-new flex items-center gap-1.5"
          >
            <RiUserAddLine className="text-white text-[14px]" />
            <span>+ Add Member</span>
          </Button>
        </div>

        {membersLoading ? (
          <div className="relative w-full min-h-[240px]" aria-busy="true">
            <Loader variant="content" />
          </div>
        ) : (
          <>
            {/* Filter */}
            <div>
              <Input
                type="text"
                placeholder="Search by name or email"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                className="w-full text-sm border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary"
              />
            </div>

            {/* Table or Empty State */}
            {members.length === 0 ? (
              <div className="text-center py-10 px-4 border border-border-primary rounded-md bg-surface-secondary/30">
                <Typography variant="h6" className="text-text-primary mb-2">
                  No team members yet
                </Typography>
                <Typography variant="text2" className="text-text-secondary mb-4 max-w-md mx-auto">
                  This team doesn't have any members yet. Add members to collaborate and assign
                  access.
                </Typography>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={onAddMember}
                  className="btn-success-new inline-flex items-center gap-1.5"
                >
                  <RiUserAddLine className="text-white text-[14px]" />
                  <Typography variant="text2" className="sr-only" component="span">
                    + Add Member
                  </Typography>
                </Button>
              </div>
            ) : filteredMembers.length === 0 ? (
              <div className="text-center py-8 text-text-secondary">
                <Typography variant="text2">No members match your search</Typography>
              </div>
            ) : (
              <div className="overflow-x-auto -mx-1">
                <table className="w-full border-collapse clients-table">
                  <thead className="bg-surface-secondary">
                    <tr>
                      <th className="text-start py-2 px-3 text-text-primary font-semibold text-sm">
                        Name
                      </th>
                      <th className="text-start py-2 px-3 text-text-primary font-semibold text-sm">
                        Email
                      </th>
                      <th className="text-start py-2 px-3 text-text-primary font-semibold text-sm">
                        Role
                      </th>
                      <th className="text-start py-2 px-3 text-text-primary font-semibold text-sm">
                        Status
                      </th>
                      <th className="text-start py-2 px-3 text-text-primary font-semibold text-sm action-col">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMembers.map((member) => (
                      <tr key={member.id} className="border-t border-border-primary">
                        <td className="py-2 px-3 text-text-primary text-sm">
                          {getTeamMemberDisplayName(member)}
                        </td>
                        <td className="py-2 px-3 text-text-primary text-sm">
                          {member.email || "—"}
                        </td>
                        <td className="py-2 px-3 text-text-primary text-sm">
                          {ROLE_LABELS[member.role] ?? member.role?.name ?? member.role ?? "—"}
                        </td>
                        <td className="py-2 px-3">
                          <Typography
                            variant="span1"
                            className={`badge rounded-full px-2.5 py-0.5 text-white text-xs font-bold ${getStatusBadgeClass(
                              member.status
                            )}`}
                          >
                            {STATUS_LABELS[member.status] ?? member.status ?? "Active"}
                          </Typography>
                        </td>
                        <td className="py-2 px-3">
                          <div className="flex gap-1 items-center">
                            <FloatingTooltip label="Edit">
                              <Button
                                onClick={() => onEditMember(member)}
                                aria-label="Edit team member"
                                size="sm"
                                className="
                                        btn-warning-new
                                        !w-7 !h-7  
                                        !p-0 
                                        !font-normal 
                                        !leading-none
                                        flex items-center justify-center
                                        border-0
                                      "
                              >
                                <RiPencilLine className="text-white text-[12px]" />
                              </Button>
                            </FloatingTooltip>

                            {member.status === "Invited" && (
                              <FloatingTooltip label="Resend Invitation">
                                <Button
                                  onClick={() => onResendInvitation(member)}
                                  aria-label="Resend Invitation"
                                  size="sm"
                                  className="btn-info-new !w-7 !h-7  !p-0 flex items-center justify-center hover:opacity-90 border-0"
                                >
                                  <RiRefreshLine className="text-white text-[12px]" />
                                </Button>
                              </FloatingTooltip>
                            )}

                            {/* Delete */}
                            <FloatingTooltip label="Delete">
                              <Button
                                onClick={() => onRemoveMember(member)}
                                aria-label="Remove team member"
                                size="sm"
                                className="btn-danger-new !w-7 !h-7  !p-0 flex items-center justify-center hover:opacity-90 border-0"
                              >
                                <RiDeleteBinFill className="text-white text-[12px]" />
                              </Button>
                            </FloatingTooltip>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </CommonModal>
  );
};

export default ViewTeamMembersModal;
